// Signing in on behalf of the iOS app.
//
// Google's sign-in asks for the person's Google passkey, and a passkey for
// google.com does not exist inside our WKWebView — the Associated Domains
// entitlement reaches only domains that vouch for our app, and google.com
// never will. The page loads, the prompt appears, and Continue can never
// succeed. Which is the real reason nobody should run OAuth in an embedded
// web view: the credential lives outside it.
//
// So the app opens the sign-in in an ASWebAuthenticationSession instead —
// Safari's context, where that passkey is — pointed at this same site with
// `?native=1`. Everything here is the other half of that: remember we are
// standing in for the app, and when a session exists, hand it back.

const FLAG = 'wa.native.handoff';
const SCHEME = 'okazip://auth';

/**
 * Uma vez por carregamento desta página, e não mais do que isso.
 *
 * Era uma marca em `sessionStorage`, que sobrevive ao carregamento — e por
 * isso à abertura seguinte da folha. A segunda vez que alguém tocava em
 * entrar, a folha abria e ficava parada: o arranque automático já se
 * considerava gasto. "Precisei de clicar três ou quatro vezes" era isto.
 *
 * Uma variável de módulo morre com a página, que é a vida certa para ela.
 */
let startedHere = false;

/** Called once on load. The flag has to outlive the trip to Google. */
export function markNativeSignIn() {
  try {
    const which = new URLSearchParams(location.search).get('native');
    if (which) sessionStorage.setItem(FLAG, which);
  } catch { /* private mode */ }
}

export function isNativeSignIn(): boolean {
  try { return !!sessionStorage.getItem(FLAG); } catch { return false; }
}

/**
 * The provider to start with, once, without waiting to be asked.
 *
 * The first version opened this site's front page inside the
 * authentication session, so the person arrived at a landing with a
 * "Get started" button and had to press it again — a window that opened
 * a window that opened Google. The session exists to do one thing, so it
 * arrives already doing it.
 *
 * O que impede o ciclo não é esta função: é quem a chama, que não arranca
 * quando a página está a voltar do provedor. Uma tentativa que falha volta
 * com essa marca posta, então não se relança; a folha seguinte é uma
 * página nova, sem ela, e arranca como deve.
 */
export function nativeSignInToStart(): 'google' | 'apple' | null {
  if (startedHere) return null;
  try {
    const which = sessionStorage.getItem(FLAG);
    if (which !== 'google' && which !== 'apple') return null;
    startedHere = true;
    return which;
  } catch { return null; }
}

/**
 * Back to the app, carrying the refresh token.
 *
 * The refresh token and not the archive key: the key is never ours to pass
 * around, and the app's web view gets it the way any browser does — from
 * the escrow, or from a passkey of ours, which does work there.
 *
 * It travels in the URL, which is the part to harden. The window is
 * milliseconds and the scheme is ours, but another app can claim the same
 * scheme on iOS, and the right shape is a one-time code exchanged against
 * the server rather than the token itself. Written down rather than
 * pretended about.
 */
export function handBackToApp(): boolean {
  if (!isNativeSignIn()) return false;
  let refresh: string | null = null;
  try { refresh = localStorage.getItem('wa.refresh'); } catch { /* private mode */ }
  if (!refresh) return false;
  try { sessionStorage.removeItem(FLAG); } catch { /* private mode */ }
  location.replace(`${SCHEME}?rt=${encodeURIComponent(refresh)}`);
  return true;
}

/**
 * E de volta também quando não deu certo.
 *
 * Sem isto, um login que falha dentro da folha do Safari deixa-a parada
 * numa página que o app não vê: quem está do lado de cá só pode fechá-la,
 * e a sessão de autenticação relata "cancelado pelo utilizador" — que é o
 * que o registo dizia enquanto o motivo verdadeiro se perdia. O app não
 * tem outro canal para dentro da folha senão este.
 */
export function handBackWithError(why: string): boolean {
  if (!isNativeSignIn()) return false;
  try { sessionStorage.removeItem(FLAG); } catch { /* private mode */ }
  location.replace(`${SCHEME}?err=${encodeURIComponent(why.slice(0, 900))}`);
  return true;
}

// ── documentos ────────────────────────────────────────────────────────────

interface Bridge { postMessage(what: unknown): void }
const bridge = (): Bridge | null =>
  (globalThis as unknown as { webkit?: { messageHandlers?: { okazip?: Bridge } } })
    .webkit?.messageHandlers?.okazip ?? null;

/**
 * Que versão da casca está do outro lado, ou 0 se não houver casca.
 *
 * A ponte não responde: `postMessage` não devolve nada e não há confirmação
 * de que alguém do lado nativo tratou a mensagem. Uma casca antiga recebe
 * `openDoc`, não reconhece a chave e descarta-a sem um erro — e a página,
 * se tomasse o envio por sucesso, fecharia a folha e deixaria o toque no
 * documento sem efeito nenhum.
 *
 * Então a casca diz no User-Agent o que sabe fazer, e isto lê-o.
 */
export function nativeShellVersion(): number {
  const m = /OkaZipApp\/(\d+)/.exec(navigator.userAgent);
  return bridge() && m ? Number(m[1]) : 0;
}

/** A primeira casca que sabe receber `openDoc`. */
const DOC_SHELL = 2;

/**
 * Entrega um documento ao visualizador do sistema.
 *
 * No app não há aba nova: um WKWebView sem `createWebViewWith` devolve nulo
 * em `window.open`, e o `target="_blank"` não faz nada. Navegar para a URL
 * do ficheiro — que era o que isto fazia — leva o PDF a substituir a
 * aplicação inteira, sem nada para voltar atrás. E quando o Service Worker
 * não intercepta a navegação, o que vem é o `index.html` do próprio app,
 * porque o Hosting reescreve tudo para lá: HTTP 200, `text/html`, e um
 * ficheiro chamado `.pdf` que é uma página web.
 *
 * O QuickLook do sistema resolve as duas coisas. Abre por cima, com a
 * aplicação intacta atrás, e traz o botão de partilha — que é por onde se
 * guarda nos Ficheiros, se envia, se imprime. É também o único sítio onde um
 * PDF se desenha mesmo: num `<iframe>` dentro de um WKWebView não se pode
 * contar com isso.
 *
 * @returns false se não houver casca, para quem chama cair no visualizador
 *   da página.
 */
/**
 * O que ainda cabe numa mensagem da ponte.
 *
 * O corpo vai em base64 — a ponte só leva JSON — e isso cresce um terço, e
 * tem de existir inteiro em memória de cada lado ao mesmo tempo. 48 MB de
 * ficheiro são uns 64 MB de texto, que é bastante e ainda assim muito acima
 * de qualquer documento que alguém mande no WhatsApp. Acima disso vale mais
 * dizer que não dá do que arriscar que o sistema mate a aplicação a meio.
 */
const BRIDGE_CAP = 48 * 1024 * 1024;

export function openDocumentNatively(name: string, mime: string, bytes: Uint8Array): boolean {
  const post = bridge();
  if (!post || nativeShellVersion() < DOC_SHELL) return false;
  if (bytes.length > BRIDGE_CAP) return false;
  // Base64 porque a ponte só leva JSON. Em pedaços, senão `String.fromCharCode`
  // estoura a pilha com um argumento por byte — um PDF de 2 MB são dois
  // milhões de argumentos.
  let ascii = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    ascii += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  post.postMessage({ openDoc: { name, mime, b64: btoa(ascii) } });
  return true;
}
